import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState({ page: 1, total: 0, total_pages: 0 });
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ product_id: '', warehouse_id: '', quantity: '', reserved_quantity: '0', reorder_level: '10' });
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [productFilterPlatform, setProductFilterPlatform] = useState('');

  const fetchInventory = async (p = 1) => {
    try {
      setLoading(true);
      const url = `/inventory/list.php?page=${p}&limit=50${filter ? '&marketplace=' + filter : ''}`;
      const res = await api.get(url);
      if (res.data.success) {
        setItems(res.data.data.inventory || []);
        setPage({ page: res.data.data.page, total: res.data.data.total, total_pages: res.data.data.total_pages });
      } else setError(res.data.message);
    } catch (e) { setError('Failed to load inventory'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchInventory(); api.get('/platforms/list.php').then(r => { if (r.data.success) setPlatforms(r.data.data || []); }).catch(() => {}); api.get('/settings/warehouses.php').then(r => { if (r.data.success) setWarehouses(r.data.data || []); }).catch(() => {}); }, [filter]);

  useEffect(() => {
    const url = `/products/list.php?page=1&limit=100${productFilterPlatform ? '&marketplace=' + productFilterPlatform : ''}`;
    api.get(url).then(r => { if (r.data.success) setProducts(r.data.data.products || []); }).catch(() => {});
  }, [productFilterPlatform]);

  const openCreate = () => { setEditing(null); setForm({ product_id: '', warehouse_id: '', quantity: '', reserved_quantity: '0', reorder_level: '10' }); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setForm({ product_id: item.product_id || '', warehouse_id: item.warehouse_id || '', quantity: item.quantity || '', reserved_quantity: item.reserved_quantity || '0', reorder_level: item.reorder_level || '10' }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, quantity: parseInt(form.quantity), reserved_quantity: parseInt(form.reserved_quantity), reorder_level: parseInt(form.reorder_level) };
      if (editing) { await api.post('/inventory/update.php', { id: editing.id, ...payload }); }
      else { await api.post('/inventory/create.php', payload); }
      setShowForm(false); fetchInventory(page.page);
    } catch (e) { setError('Failed to save inventory'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try { await api.post('/inventory/delete.php', { id }); fetchInventory(page.page); }
    catch (e) { setError('Failed to delete'); }
  };

  const stockStatus = (qty, reorder) => {
    if (!qty && qty !== 0) return { label: 'N/A', color: 'bg-gray-100 text-gray-800' };
    if (qty === 0) return { label: 'Out of Stock', color: 'bg-red-100 text-red-800' };
    if (qty <= reorder) return { label: 'Low Stock', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'In Stock', color: 'bg-green-100 text-green-800' };
  };

  const typeColors = { amazon: 'bg-orange-100 text-orange-800', flipkart: 'bg-blue-100 text-blue-800', meesho: 'bg-purple-100 text-purple-800' };

  if (loading && !items.length) return <div className="text-center py-20 text-gray-600">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Inventory</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Total: {page.total} records</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">Add Stock</button>
      </div>

      <div className="flex gap-2 items-center">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Platform:</label>
        <select value={filter} onChange={e => { setFilter(e.target.value); setPage(p => ({...p, page: 1})); }} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
          <option value="">All Platforms</option>
          <option value="amazon">Amazon</option>
          <option value="flipkart">Flipkart</option>
          <option value="meesho">Meesho</option>
        </select>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Warehouse</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reserved</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {items.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-500">No inventory records.</td></tr>
              ) : items.map(item => {
                const st = stockStatus(item.quantity, item.reorder_level);
                const isVariant = item.parent_id;
                const displayName = item.parent_name
                  ? <><span className="text-gray-400 text-xs">{item.parent_name} &gt;</span> {item.product_name}</>
                  : item.product_name;
                const variantInfo = [item.size, item.color].filter(Boolean).join(', ');
                return (
                  <tr key={item.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isVariant ? '' : 'bg-blue-50/50 dark:bg-blue-900/10'}`}>
                    <td className="px-6 py-4">
                      {item.platforms ? item.platforms.split(',').map(pt => (
                        <span key={pt.trim()} className={`inline-block px-2 py-1 text-xs font-medium rounded-full capitalize mr-1 ${typeColors[pt.trim()] || 'bg-gray-100'}`}>{pt.trim()}</span>
                      )) : <span className="text-xs text-gray-400">-</span>}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {isVariant ? <span className="pl-4 text-gray-400 mr-1">└─</span> : null}
                      {displayName}
                      {variantInfo ? <span className="text-xs text-gray-500 ml-1">({variantInfo})</span> : null}
                      {!isVariant && <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">Main</span>}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-600">{item.sku || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{item.warehouse_name || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm font-semibold">{item.quantity ?? 0}</td>
                    <td className="px-6 py-4 text-sm text-orange-600">{item.reserved_quantity ?? 0}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${st.color}`}>{st.label}</span></td>
                    <td className="px-6 py-4 text-sm space-x-2">
                      <button onClick={() => openEdit(item)} className="text-primary-600">Edit</button>
                      <button onClick={() => handleDelete(item.id)} className="text-red-600">Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {page.total_pages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <span className="text-sm text-gray-600">Page {page.page} of {page.total_pages}</span>
          <div className="flex gap-2">
            <button onClick={() => fetchInventory(page.page - 1)} disabled={page.page === 1} className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50">Previous</button>
            <button onClick={() => fetchInventory(page.page + 1)} disabled={page.page === page.total_pages} className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Stock' : 'Add Stock'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Product *</label>
              <div className="flex gap-2 mb-2">
                <select value={productFilterPlatform} onChange={e => setProductFilterPlatform(e.target.value)} className="w-1/2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
                  <option value="">All Platforms</option>
                  {platforms.map(pl => <option key={pl.id} value={pl.marketplace_type}>{pl.marketplace_type.charAt(0).toUpperCase() + pl.marketplace_type.slice(1)} - {pl.account_name}</option>)}
                </select>
                <select value={form.product_id} onChange={e => setForm({...form, product_id: e.target.value})} className="w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required>
                  <option value="">Select</option>
                  {products.filter(p => p.parent_id).length > 0 && (
                    <optgroup label="Variants">
                      {products.filter(p => p.parent_id).map(p => {
                        const parent = products.find(x => String(x.id) === String(p.parent_id));
                        const label = parent ? `${parent.name} > ${p.name}` : p.name;
                        const variantInfo = [p.size, p.color].filter(Boolean).join(', ');
                        return <option key={p.id} value={p.id}>{label} ({p.sku}){variantInfo ? ` - ${variantInfo}` : ''}</option>;
                      })}
                    </optgroup>
                  )}
                  {products.filter(p => !p.parent_id).length > 0 && (
                    <optgroup label="Main Products">
                      {products.filter(p => !p.parent_id).map(p => (
                        <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Warehouse *</label>
              <select value={form.warehouse_id} onChange={e => setForm({...form, warehouse_id: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required>
                <option value="">Select</option>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Quantity *</label><input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required /></div>
            <div><label className="block text-sm font-medium mb-1">Reorder Level</label><input type="number" value={form.reorder_level} onChange={e => setForm({...form, reorder_level: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">{editing ? 'Update' : 'Add'} Stock</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Inventory;
